import moment from 'moment';
import {
  CLICK_ACTIONS,
  DATE_FORMAT,
  DEFAULT_OPTION,
  LEAVE_PAGE_TYPE,
  LEAVE_STATUSES,
  VIEW_ATTENDEES,
} from '../../utils/Constant';
import {
  AttendanceFilterType,
  AttendanceItemType,
  ColumnHeaderType,
  EmployeeListItemType,
  LeaveDetails,
  LeaveFilterType,
  LeaveItemType,
  LocationFilterInitialState,
  LoggedUserType,
  UserType,
} from '../../utils/Types';
import {
  blankValueGetter,
  formattedDate,
  formattedDateWithFormat,
  handleViewAttendeesActionColors,
  isAPIFailed,
  isAPISuccess,
  nameValueGetter,
  noOfDaysValueGetter,
} from '../../utils/Util';
import { fetchVillagesOfDistricts } from '../../features/locationFilterSlice';
import { ThunkDispatch } from 'redux-thunk';
import { toast } from 'react-toastify';
import { STATE_ACCESS_ROLES } from '../../utils/RBAC';

/**
 *  Changes the colors based on the given status in table listing.
 */
export const handleLeaveHistoryActionColors = (data: any) => {
  const value = data?.requestStatus;
  if (value == LEAVE_STATUSES?.REJECT) {
    return 'text-danger';
  } else if (value == LEAVE_STATUSES?.APPROVE) {
    return 'text-success';
  } else {
    return '';
  }
};

export const VIEW_ATTENDEES_HEADER: ColumnHeaderType[] = [
  {
    name: 'Employee Id',
    field: 'employeeUniqueId',
    sortable: false,
    showProfilePhoto: true,
  },
  {
    name: 'Employee Name',
    field: 'fullname',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Zone',
    field: 'zoneId',
    sortable: false,
    valueFromLocalStorage: true,
  },
  {
    name: 'State',
    field: 'stateId',
    sortable: false,
    valueFromLocalStorage: true,
  },
  {
    name: 'CheckIn Date & Time',
    field: 'checkInTime',
    sortable: false,
    valueGetter: (data: any, field?: string) => {
      return formattedDateWithFormat(
        String(data[field as keyof any]),
        DATE_FORMAT.DD_MM_YYYY_hh_mm_A
      );
    },
  },
  {
    name: 'Check-in Location',
    field: 'checkInLocation',
    sortable: false,
    clickable: true,
    action: CLICK_ACTIONS.ATTENDEES_CHECK_IN_LIST,
    classNameGetter: () => 'text-primary',
  },
  {
    name: 'Check-out Date & Time',
    field: 'checkOutTime',
    sortable: false,
    valueGetter: (data: any, field?: string) => {
      return formattedDateWithFormat(
        String(data[field as keyof any]),
        DATE_FORMAT.DD_MM_YYYY_hh_mm_A
      );
    },
  },
  {
    name: 'Status',
    field: 'approvalStatusName',
    clickable: true,
    sortable: false,
    // actionGetter: viewAttendanceActionGetter,
    action: CLICK_ACTIONS.APPROVE,
    classNameGetter: handleViewAttendeesActionColors,
    valueGetter: (data: any, field?: string) => {
      if (data?.approvalStatus == VIEW_ATTENDEES?.ACTION_REQUIRED) {
        return 'Action Required';
      }
      return String(data[field as keyof any]).replace('_', ' ') || '-';
    },
  },
];
export const LEAVE_HISTORY_HEADER: ColumnHeaderType[] = [
  {
    name: 'Employee Id',
    field: 'employeeUniqueId',
    sortable: false,
    showProfilePhoto: true,
    clickable: true,
    action: CLICK_ACTIONS.APPROVE,
  },
  {
    name: 'Employee Name',
    field: 'name',
    sortable: false,
    toolTipAfter: 25,
    valueGetter: nameValueGetter,
  },
  {
    name: 'Leave Type',
    field: 'leaveTypeName',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Leave Request (days)',
    field: 'leaveDays',
    sortable: false,
    valueGetter: noOfDaysValueGetter,
  },
  {
    name: 'Leave Dates',
    field: 'startDate',
    sortable: false,
    valueGetter: (data: any, field?: string) => {
      const endDate = data?.endDate;
      return `${formattedDate(String(data[field as keyof any]))} - ${formattedDate(endDate)}`;
    },
  },

  {
    name: 'Status',
    field: 'requestStatusName',
    sortable: false,
    classNameGetter: handleLeaveHistoryActionColors,
  },
  {
    name: 'Approved By',
    field: 'approver',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'Approved Date',
    field: 'approvedDate',
    sortable: false,
    valueGetter: (data: any, field?: string) => {
      return formattedDate(String(data[field as keyof any]));
    },
  },
];

export const APPROVE_LEAVE_HEADER: ColumnHeaderType[] = [
  {
    name: 'Employee Id',
    field: 'employeeUniqueId',
    sortable: false,
    showProfilePhoto: true,
    clickable: true,
    action: CLICK_ACTIONS.APPROVE,
  },
  {
    name: 'Employee Name',
    field: 'name',
    sortable: false,
    toolTipAfter: 25,
    valueGetter: nameValueGetter,
  },
  {
    name: 'Leave Type',
    field: 'leaveTypeName',
    sortable: false,
    valueGetter: blankValueGetter,
  },
  {
    name: 'City/Village',
    field: 'city',
    sortable: false,
    valueGetter: blankValueGetter,
    valueFromLocalStorage: true,
  },
  {
    name: 'Leave Request (days)',
    field: 'leaveDays',
    sortable: false,
    valueGetter: noOfDaysValueGetter,
  },
  {
    name: 'Leave Dates',
    field: 'startDate',
    sortable: false,
    valueGetter: (data: any, field?: string) => {
      const endDate = data?.endDate;
      return `${formattedDate(String(data[field as keyof any]))} - ${formattedDate(endDate)}`;
    },
  },
  {
    name: 'Status',
    field: 'requestStatusName',
    sortable: false,
  },
];

/**
 * Creates the payload for fetching the leave list.
 */
export const getLeaveListParams = (
  currentPage: number,
  pageSize: number,
  type: string | null,
  user: LoggedUserType,
  userDetails: UserType,
  selectedFilter?: LeaveFilterType
) => {
  const data: any = {
    pageNumber: currentPage,
    pagePerCount: pageSize,
    fromDate: moment().format('YYYY-MM-DD'),
    toDate: moment().endOf('year').format('YYYY-MM-DD'),
    status: null,
  };
  if (type === LEAVE_PAGE_TYPE.APPROVE_LEAVE) {
    data.status = LEAVE_STATUSES?.PENDING;
  }
  if (
    STATE_ACCESS_ROLES.includes(user?.roleId) &&
    userDetails?.reportingZones?.length
  ) {
    data.zone = Number(userDetails?.reportingZones[0]);
  }
  if (selectedFilter?.employeeUniqueId)
    data.employeeUniqueId = selectedFilter.employeeUniqueId;
  if (selectedFilter?.employeeName)
    data.employeeName = selectedFilter.employeeName;
  if (selectedFilter?.zone?.value)
    data.zone = Number(selectedFilter.zone.value);
  if (selectedFilter?.state?.value)
    data.state = Number(selectedFilter.state.value);
  if (selectedFilter?.role?.value)
    data.role = Number(selectedFilter.role.value);
  if (selectedFilter?.leaveType?.value)
    data.leaveType = selectedFilter.leaveType.value;
  if (selectedFilter?.status?.value) data.status = selectedFilter.status.value;
  if (selectedFilter?.fromDate)
    data.fromDate = moment(selectedFilter.fromDate).format('YYYY-MM-DD');
  if (selectedFilter?.toDate)
    data.toDate = moment(selectedFilter.toDate).format('YYYY-MM-DD');
  return data;
};

/**
 *  Fetches the details of the city or village to display in the table.
 */
export const requestCities = (
  leaveDataList: LeaveItemType[] | EmployeeListItemType[],
  locationData: LocationFilterInitialState,
  dispatch: ThunkDispatch<any, any, any>
) => {
  if (leaveDataList?.length && locationData?.cityOptions) {
    // Available state and city
    const districtIdAsKey = Object.keys(locationData.cityOptions);
    const cityNotFoundArray: string[] = [];
    for (const element of leaveDataList) {
      const leave = element;
      if (!districtIdAsKey.includes(String(leave.district))) {
        if (!cityNotFoundArray.includes(String(leave.district))) {
          cityNotFoundArray.push(String(leave.district));
        }
      }
    }
    if (cityNotFoundArray.length) {
      dispatch(
        fetchVillagesOfDistricts({
          districtId: cityNotFoundArray,
        })
      );
    }
  }
};

/**
 *  Handles the click event for the Check-in Location and Status elements.
 */
export const onTableAction = (
  param: AttendanceItemType,
  header: ColumnHeaderType,
  isModalOpen: boolean,
  setSelectedUser: (data: AttendanceItemType) => void,
  setIsModalOpen: (open: boolean) => void
) => {
  if (
    header.action === CLICK_ACTIONS.ATTENDEES_CHECK_IN_LIST &&
    param?.checkInLocation
  ) {
    window.open(`https://maps.google.com?q=${param?.checkInLocation}`);
  }
  if (header?.action === CLICK_ACTIONS.APPROVE) {
    setSelectedUser(param);
    setIsModalOpen(!isModalOpen);
  }
};
/**
 * Sets the default status option for the "View Attendees" page filter.
 */
export const getMarkedStatusDefault = (
  attendeesStatusDropOptions: any[],
  filter: AttendanceFilterType
) => {
  let markedStatusOption = DEFAULT_OPTION;
  if (attendeesStatusDropOptions?.length) {
    const markedDropLabel = attendeesStatusDropOptions?.filter(
      (item: any) => item?.value === VIEW_ATTENDEES?.ACTION_REQUIRED
    );
    if (markedDropLabel?.length) {
      markedStatusOption = markedDropLabel?.[0];
    }
  }

  return {
    ...filter,
    status: markedStatusOption,
  };
};

/**
 * Creates a payload to fetch the attendance list.
 */
export const getAttendanceRequestParam = (
  filter: AttendanceFilterType,
  currentPage: number,
  pageSize: number,
  user: LoggedUserType,
  userDetails: UserType
) => {
  const data: any = {
    pageNumber: currentPage,
    pagePerCount: pageSize,
    status: VIEW_ATTENDEES?.ACTION_REQUIRED, //for the show only action required attendance
  };
  if (
    STATE_ACCESS_ROLES.includes(user?.roleId) &&
    userDetails?.reportingZones?.length
  ) {
    data.zone = Number(userDetails?.reportingZones[0]);
  }
  if (filter?.candidateId) {
    data.candidateId = filter?.candidateId;
  }
  if (filter?.employeeName) {
    data.employeeName = filter?.employeeName;
  }
  if (filter?.role?.value) {
    data.role = filter.role.value;
  }
  if (filter?.zone?.value) {
    data.zone = filter.zone.value;
  }
  if (filter?.state?.value) {
    data.state = filter.state.value;
  }
  if (filter?.district?.value) {
    data.district = filter.district.value;
  }
  if (filter?.city?.value) {
    data.city = filter.city.value;
  }
  if (filter?.date) {
    data.date = moment(filter.date).format('YYYY-MM-DD');
  }
  if (filter?.status?.value) {
    data.status = filter.status.value;
  }
  return data;
};

/**
 * Validates and creates the payload for approving a leave request.
 */
export const getLeaveApproveParams = (
  type: string,
  comment: string,
  leaveDetails: LeaveDetails | null
) => {
  if (!comment.trim() && type === 'reject') {
    toast.error('Comment is required');
    return null;
  }
  const data: any = {
    leaveRequestId: leaveDetails?.leaveRequestId,
    leaveStatus: null,
    comments: comment || '',
  };
  if (type === 'reject') {
    data.leaveStatus = 1; //for reject leave
  } else {
    data.leaveStatus = 0; //for approve leave
  }
  return data;
};

/**
 * Displays a toast message based on the response from the API.
 */
export const onApproveRejectSuccess = (
  fulfilled: any,
  toggleApproveModal: () => void,
  setShowLoader: (value: boolean) => void
) => {
  if (isAPIFailed(fulfilled?.payload)) {
    toast.error(fulfilled?.payload.responseInformation.responseMessage);
  } else if (isAPISuccess(fulfilled?.payload)) {
    toast.success(fulfilled?.payload.responseInformation.responseMessage);
    toggleApproveModal();
  }
  setShowLoader(false);
};
